import { toLocalISOString } from 'shared/utils/date-time';
import { toMergeAsBasicName } from 'shared/utils/strings';

import { verifyUserType } from 'features/print-management/order-invoice-printing';
import { transformOrderTotalsDetails } from 'shared/infra/orders/orderDetail.utils';

import type {
  OrderDetailToPrint,
  ToppingToPrint,
} from 'features/print-management/order-invoice-printing';
import type { OrderDetailRawResp } from './orderToPrint.response';

function toPrintOrder(order: OrderDetailRawResp | null): OrderDetailToPrint {
  if (!order) return { id: NaN } as OrderDetailToPrint;

  const firstNames = (order.user.first_name ?? '').trim();
  const lastNames = (order.user.last_name ?? '').trim();
  const basicName = toMergeAsBasicName({ firstNames, lastNames });

  const transformPhoneDefault = (v: string) => (v === '********' ? '' : v);

  const transformAddressDefault = (v: string) => (v === ' --- ' ? '' : v);

  return {
    id: order.id,
    createdAt: toLocalISOString(new Date(order.created_at.replace(' ', 'T'))),
    deliveryMethod: order.delivery_method,
    paymentMethod: order.payment_method.trim(),
    primeShippingDiscount: order.prime_shipping_discount,
    specialOrder: order.special_order ?? [],
    user: {
      name: {
        first: firstNames,
        last: lastNames,
        basic: basicName,
      },
      type: verifyUserType({
        isNewUser: order.user.is_new_user,
        highValueUser: order.user.high_value_user,
      }),
      contact: {
        phone: transformPhoneDefault(order.user.phone),
        address: {
          base: transformAddressDefault(order.user.address),
          description: transformAddressDefault(order.user.address_description),
        },
        zipCode: order.user.zip_code ?? '',
        neighborhood: order.user.neighborhood ?? '',
      },
    },
    store: {
      id: order.stores[0].store_id,
      name: order.stores[0].name,
    },
    products: order.products.map(p => ({
      id: p.id,
      orderProductID: p.order_product_id,
      name: p.name,
      units: p.units,
      report: {
        defectRate: {
          enabled: p.has_defect_rate_report,
        },
      },
      price: {
        totalProduct: p.total_price_product,
        totalToppings: p.total_price_toppings,
      },
      /*toppingsByCategory: p.groupedToppings.map(tc => ({
        name: tc.name.toUpperCase(),
        toppings: tc.toppings.map(t => ({
          id: t.id,
          description: t.description,
          units: t.units,
          price: {
            base: t.price,
            total: t.total_price,
            real: t.total_real_price,
          },
        })),
      })),*/
      toppings: p.toppings
        .map(
          (t): ToppingToPrint => ({
            id: t.id,
            index: t.topping_category_index,
            toppingNodeId: t?.order_product_topping_id ?? NaN,
            parentID: t.parent_id ?? NaN,
            children: [],
            name: t.topping_category.toUpperCase(),
            description: t.description,
            units: t.units,
            price: {
              base: t.price,
              total: t.total_price,
              real: t.total_real_price,
            },
          }),
        )
        .sort((a, b) => a.index - b.index),
      /* Grouped topping by name (category) using Map:
        src/shared/utils/orders/toppings.util.ts
        .reduce((acc, t) => {
          const _groupT = acc.find(a => a[0] === t.name);
          if (_groupT === undefined) acc.push([t.name, [t]]);
          else _groupT[1].push(t);
          return [...acc];
        }, [] as [ToppingToPrint['name'], ToppingToPrint[]][])
        .flatMap(gt => gt[1]),*/
      replace: {
        suggestion: p.replacement_suggestion,
      },
      comments: p.comments,
      globalOffer: {
        condition: {
          tagPromo:
            p.global_offers?.[0]?.conditions?.[0]?.tag_promo?.trim() ?? '',
        },
      },
    })),
    whims:
      order.whims?.map(w => ({
        id: w.id,
        name: w.name,
        price: w.price ?? 0,
      })) ?? [],
    totals: {
      main: {
        id: order.order_totals.total.id,
        label: order.order_totals.total.text ?? '',
        price: order.order_totals.total.value ?? 0.0,
        isZero: order.order_totals.total.value === 0,
        isNegative: order.order_totals.total.value < 0,
      },
      detail: transformOrderTotalsDetails(order.order_totals.details),
      // When using fields other than 'message', check their adaptation
      error: order.order_totals.errors,
    },
    cashReceivedFromUser: order.cash_given_by_user ?? NaN,
    handshake: {
      manualCode: order.qr_information?.product_confirmation_code ?? '', //'4396',
      qrCode: order.qr_information?.qr_code ?? '',
      // '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',
    },
  };
}

export const orderToPrintAdapter = {
  toPrintOrder,
};
